import { useNavigate } from 'react-router-dom';
import { useAnomalyDetect, useAnomalyHistory } from '../api/intelligence';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400',
  high: 'bg-orange-500/20 text-orange-400',
  medium: 'bg-amber-500/20 text-amber-400',
  low: 'bg-green-500/20 text-green-400',
};

const ANOMALY_LABELS: Record<string, string> = {
  sudden_connectivity_spike: 'Pic de connexions',
  isolated_to_central: 'Isole → Central',
  risk_score_jump: 'Bond de risque',
};

export default function AnomalyPage() {
  const navigate = useNavigate();
  const detectMutation = useAnomalyDetect();
  const { data: history, isLoading: historyLoading } = useAnomalyHistory();

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Detection d'anomalies</h1>
          <p className="text-sm text-gray-500 mt-1">Comportements inhabituels dans le graphe d'entites</p>
        </div>
        <button
          onClick={() => detectMutation.mutate(undefined)}
          disabled={detectMutation.isPending}
          className="px-5 py-2 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 disabled:opacity-50 transition-colors"
        >
          {detectMutation.isPending ? 'Analyse en cours...' : 'Lancer la detection'}
        </button>
      </div>

      {/* Detection results */}
      {detectMutation.data && (
        <div className="space-y-3">
          <div className="glass-panel p-4 text-center">
            <p className="text-3xl font-bold text-cyber-cyan">{detectMutation.data.anomalies.length}</p>
            <p className="text-xs text-gray-500">anomalies detectees sur {detectMutation.data.total_analyzed} entites</p>
          </div>

          {detectMutation.data.anomalies.map((a, i) => (
            <div
              key={i}
              className="glass-panel p-4 flex items-center gap-4 cursor-pointer hover:border-cyber-cyan/30 transition-colors"
              onClick={() => navigate(`/entities/${a.entity_id}`)}
            >
              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                a.severity === 'critical' ? 'bg-red-500' :
                a.severity === 'high' ? 'bg-orange-500' :
                a.severity === 'medium' ? 'bg-amber-500' : 'bg-green-500'
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium text-gray-200">{a.entity_name}</h4>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${SEVERITY_COLORS[a.severity] || ''}`}>
                    {a.severity}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{a.details}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="text-xs bg-surface-tertiary text-gray-400 px-2 py-0.5 rounded">
                  {ANOMALY_LABELS[a.anomaly_type] || a.anomaly_type}
                </span>
                <p className="text-xs text-gray-600 mt-1">Score: {Math.round(a.score * 100)}%</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      <div>
        <h2 className="text-lg font-semibold text-gray-200 mb-3">Historique</h2>
        {historyLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
          </div>
        )}
        {history && history.length > 0 && (
          <div className="space-y-2">
            {history.slice(0, 30).map((a, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-surface-tertiary/30 hover:bg-surface-tertiary/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/entities/${a.entity_id}`)}
              >
                <span className={`px-2 py-0.5 text-xs rounded-full ${SEVERITY_COLORS[a.severity] || ''}`}>
                  {a.severity}
                </span>
                <span className="text-sm text-gray-300 flex-1">{a.entity_name}</span>
                <span className="text-xs text-gray-500">{ANOMALY_LABELS[a.anomaly_type] || a.anomaly_type}</span>
                <span className="text-xs text-gray-600">{new Date(a.detected_at).toLocaleDateString('fr-FR')}</span>
              </div>
            ))}
          </div>
        )}
        {!historyLoading && (!history || history.length === 0) && (
          <p className="text-center text-gray-500 py-8">Aucun historique d'anomalies</p>
        )}
      </div>
    </div>
  );
}
